import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"
import {
  CaretDown,
  CaretRight,
  Copy,
  Lock,
  Plus,
  Sliders,
  Storefront,
  Trash,
} from "@phosphor-icons/react"

import { Button, IconButton, Input, SegmentedControl } from "~/components/design-system/components"
import { ConfirmationDialog } from "~/components/reusable/ConfirmationDialog"
import HeaderTabs, { type HeaderTab } from "~/components/reusable/HeaderTabs"
import { showErrorToast } from "~/utils/toaster"
import { cn, omit } from "~/utils/helpers"
import { pluralize } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"
import { NEW_RULE_ID, reviewPeriodText } from "~/utils/allocationRuleDrafts"
import {
  reviewPeriodDaysFrom,
  reviewPeriodRange,
  type RuleDepartureSchedule,
} from "~/utils/allocationRuleMaths"
import { weekdayToRruleDay } from "~/utils/rruleWeekdays"
import {
  RULE_DEFAULTS,
  WEEKDAYS,
  computeStockLevels,
  deriveReviewCycleDays,
  effectiveReviewCycleDays,
  type RuleFieldChange,
  type RuleParams,
} from "~/utils/replenishmentRules"
import {
  useAllocationPackages,
  useAllocationStores,
  useRuleImpactLines,
  useRateOfSaleSettings,
  useRuleMutations,
  type StoreWithStatus,
} from "../useAllocationData"
import { ruleImpact } from "./ruleImpact"
import {
  editorParamsToRuleValues,
  useRuleEditorRules,
  type EditorRuleView,
} from "./ruleEditorAdapter"
import {
  StoreAssignmentTable,
  type StoreAssignmentColumn,
  type StoreAssignmentRow,
} from "../StoreAssignmentTable"
import { DraftCommandbar, useGuardedSwitch, type SaveCallbacks } from "../DraftCommandbar"
import {
  paramsWithCards,
  assignmentsSavedWith,
  ruleAssignmentChanges,
  ruleDraftChanges,
  usePendingAssignments,
  useSetupDrafts,
  noteRunAfterCurrent,
  useStagedChanges,
  type CardDraft,
  type RuleDraft as Draft,
} from "./setupDrafts"

const RULE_DETAIL_TABS: HeaderTab[] = [
  { label: "Logic", icon: <Sliders /> },
  { label: "Stores", icon: <Storefront /> },
]

function paramsOf(rule: {
  thresholdMode: string
  rosWindowDays: number
  minEligibleDays: number
  cycleMode: string
  reviewCycleDays: number
  deliveryDays: string[]
  repeatIntervalWeeks: number
  cutoffTime: string
  safetyCoverDays: number
  overstockFactor: number
  unitMin: number
  unitOpt: number
  unitOver: number
}): RuleParams {
  return {
    thresholdMode: rule.thresholdMode === "units" ? "units" : "days",
    rosWindowDays: rule.rosWindowDays,
    minEligibleDays: rule.minEligibleDays,
    cycleMode: rule.cycleMode === "schedule" ? "schedule" : "fixed",
    reviewCycleDays: rule.reviewCycleDays,
    deliveryDays: rule.deliveryDays,
    repeatIntervalWeeks: rule.repeatIntervalWeeks,
    cutoffTime: rule.cutoffTime,
    safetyCoverDays: rule.safetyCoverDays,
    overstockFactor: rule.overstockFactor,
    unitMin: rule.unitMin,
    unitOpt: rule.unitOpt,
    unitOver: rule.unitOver,
  }
}

const dec1 = (n: number) => Number(n).toFixed(1)

/** The editor's weekly departure calendar in the stored rule's schedule shape. */
function scheduleOf(params: RuleParams): RuleDepartureSchedule {
  return {
    freq: "WEEKLY",
    byDay: params.deliveryDays.map(weekdayToRruleDay),
    interval: params.repeatIntervalWeeks,
    cutoff: params.cutoffTime,
  }
}

const hasCalendar = (params: RuleParams) =>
  params.cycleMode === "schedule" && params.deliveryDays.length > 0

/** The review period a run started now orders for, as the engine does: the gap after the next departure. */
function reviewDaysNow(params: RuleParams, now: Date): number {
  return hasCalendar(params)
    ? reviewPeriodDaysFrom(scheduleOf(params), now)
    : effectiveReviewCycleDays(params)
}

/**
 * The order-up-to cover a run needs on the longest gap between departures,
 * when that is above the overstock cover; null when the overstock level never caps it.
 */
function longestGapOverstockClash(params: RuleParams, minDays: number, overDays: number) {
  if (!hasCalendar(params)) return null
  const { max } = reviewPeriodRange(scheduleOf(params))
  const cover = minDays + max
  return cover > overDays ? { gap: max, cover } : null
}

function cycleSummary(params: RuleParams): string {
  if (params.cycleMode === "schedule" && params.deliveryDays.length > 0) {
    return params.deliveryDays.join("/")
  }
  return `every ${dec1(effectiveReviewCycleDays(params))}d`
}

function ruleSavedMessage(name: string, isNew: boolean, changeCount: number) {
  if (!isNew) return `${pluralize(changeCount, "change")} saved to ${name}`
  if (changeCount === 0) return `${name} created with default values`
  return `${name} created with ${pluralize(changeCount, "change")}`
}

export default function SetupRules({
  focusRuleId,
  onGoToRateOfSale,
}: {
  focusRuleId?: string
  onGoToRateOfSale: () => void
}) {
  const { rules, isLoading } = useRuleEditorRules()
  const { stores, isLoading: storesLoading } = useAllocationStores()
  const { packages } = useAllocationPackages()
  const mutations = useRuleMutations()

  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(focusRuleId ?? null)
  const [ruleTab, setRuleTab] = useState<"logic" | "stores">("logic")
  const { drafts, setDrafts, cardDrafts, setCardDrafts, assignDraft, setAssignDraft } =
    useSetupDrafts()
  const stagedChanges = useStagedChanges()
  const [simLead, setSimLead] = useState<number | null>(null)

  const isNew = selectedRuleId === NEW_RULE_ID
  const selectedRule: EditorRuleView | null = isNew
    ? null
    : (rules.find((r) => r.id === selectedRuleId) ?? rules[0] ?? null)
  const activeId = isNew ? NEW_RULE_ID : (selectedRule?.id ?? null)

  const savedParams: RuleParams = selectedRule ? paramsOf(selectedRule) : RULE_DEFAULTS
  const draft = activeId ? (drafts[activeId] ?? {}) : {}
  const effective: RuleParams = { ...savedParams, ...draft }
  const effectiveName = draft.name ?? (isNew ? "Untitled rule" : (selectedRule?.name ?? ""))
  // Never bake the draft hint into the persisted description.
  const effectiveDescription =
    draft.description !== undefined ? draft.description : (selectedRule?.description ?? null)

  const saving = mutations.saveRule.isPending

  const setDraft = (patch: Draft) => {
    if (!activeId || saving) return
    setDrafts((prev) => ({ ...prev, [activeId]: { ...prev[activeId], ...patch } }))
  }

  const cardDraft = activeId ? (cardDrafts[activeId] ?? null) : null
  const targetsActive = cardDraft !== null

  // First card edit opens a card draft seeded from the current settings; the
  // settings numbers themselves are never written until save.
  const updateCardDraft = (patch: Partial<CardDraft>) => {
    if (!activeId || saving) return
    const coverOptSeed = effective.safetyCoverDays + effectiveReviewCycleDays(effective)
    setCardDrafts((prev) => ({
      ...prev,
      [activeId]: {
        coverMinDays: effective.safetyCoverDays,
        coverOptDays: coverOptSeed,
        coverOverDays: coverOptSeed * effective.overstockFactor,
        ...prev[activeId],
        ...patch,
      },
    }))
  }

  // "Edit settings instead": discard the card values, back to the settings
  // numbers (which were never touched).
  const handleEditSettingsInstead = () => {
    if (!activeId || saving) return
    setCardDrafts((prev) => ({ ...prev, [activeId]: null }))
  }

  // The params saving/preview would produce (card values convert to the
  // coupled settings fields only on save).
  const effectiveWithCards: RuleParams = paramsWithCards(effective, cardDraft)

  const handleModeChange = (patch: Draft) => {
    if (!activeId || saving) return
    // Switching target basis drops any open card draft — it belongs to the
    // other mode's cards.
    setCardDrafts((prev) => ({ ...prev, [activeId]: null }))
    setDraft(patch)
  }

  const storeRuleOf = useCallback(
    (store: StoreWithStatus): string | null =>
      assignDraft[store.id] !== undefined ? assignDraft[store.id]! : store.replenishmentRuleId,
    [assignDraft],
  )

  const assignedStores = useMemo(
    () => (activeId ? stores.filter((s) => storeRuleOf(s) === activeId) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stores, assignDraft, activeId],
  )

  const changes: RuleFieldChange[] = (activeId && stagedChanges.get(activeId)) || []

  const pendingAssignments = assignmentsSavedWith(activeId, usePendingAssignments())
  const draftChanges = [
    ...(activeId ? ruleDraftChanges(activeId, changes, drafts) : []),
    ...ruleAssignmentChanges(pendingAssignments, rules, drafts),
  ]
  const discardDraft = () => {
    if (!activeId) return
    setDrafts((prev) => omit(prev, activeId))
    setAssignDraft((prev) => omit(prev, ...pendingAssignments.map((move) => move.storeId)))
    setCardDrafts((prev) => ({ ...prev, [activeId]: null }))
    if (isNew) setSelectedRuleId(rules[0]?.id ?? null)
  }

  // One rule's changes are staged at a time: its Commandbar saves only that rule.
  const ruleSwitch = useGuardedSwitch(draftChanges.length > 0, saving)
  const selectRule = (ruleId: string) => {
    if (ruleId !== activeId) ruleSwitch.run(() => setSelectedRuleId(ruleId))
  }

  const save = (callbacks?: SaveCallbacks) => {
    if (!activeId) return
    const payload = {
      ...(isNew ? {} : { id: activeId }),
      ...editorParamsToRuleValues(effectiveWithCards, {
        name: effectiveName,
        description: effectiveDescription,
        dtstart: selectedRule?.dtstart,
        fastestTransitDays: selectedRule?.fastestTransitDays,
      }),
    }
    const assignments = pendingAssignments.map(({ storeId, toRuleId }) => ({
      storeId,
      ruleId: toRuleId,
    }))
    mutations.saveRule.mutate(
      { ...payload, assignments },
      {
        onError: (error) => {
          showErrorToast(error)
          callbacks?.onFailed?.()
        },
        onSuccess: (result) => {
          setDrafts((prev) => omit(prev, activeId))
          setCardDrafts((prev) => ({ ...prev, [activeId]: null }))
          setAssignDraft((prev) => omit(prev, ...assignments.map((move) => move.storeId)))
          setSelectedRuleId(result.ruleId)
          toast.success(ruleSavedMessage(effectiveName, isNew, result.changeCount))
          noteRunAfterCurrent(result.run)
          callbacks?.onSaved?.()
        },
      },
    )
  }

  const addRule = () => {
    if (isNew) return
    ruleSwitch.run(() => {
      setSelectedRuleId(NEW_RULE_ID)
      setRuleTab("logic")
      setDrafts((prev) => ({ ...prev, [NEW_RULE_ID]: { name: "Untitled rule" } }))
    })
  }

  if (isLoading) {
    return <div className="paragraph-sm p-6 text-fg-muted">Loading rules…</div>
  }

  return (
    <div className="relative flex min-h-0 flex-1 overflow-hidden rounded-lg border border-base bg-base">
      {/* Sidebar */}
      <div className="flex w-80 shrink-0 flex-col border-r border-base">
        <div className="flex items-center gap-2 border-b border-base px-3 py-2.5">
          <span className="label-sm flex-1 text-fg-muted">Replenishment rules</span>
          <Button size="sm" variant="neutral" onClick={addRule}>
            <Plus size={12} />
            New
          </Button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-2">
          {rules.map((rule) => {
            const params = paramsOf(rule)
            const selected = activeId === rule.id
            const staged = stagedChanges.has(rule.id)
            return (
              <button
                key={rule.id}
                onClick={() => selectRule(rule.id)}
                className={cn(
                  "rounded-lg border p-2.5 text-left",
                  selected ? "border-strong bg-highlight" : "border-base bg-base hover:bg-subtle",
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="label-base flex-1 truncate">{rule.name}</span>
                  {staged && (
                    <span className="paragraph-xs flex items-center gap-1 text-badge-orange-fg-text">
                      <span className="size-1.5 rounded-full bg-badge-orange-fg-text" />
                      Pending
                    </span>
                  )}
                  <span className="paragraph-xs tabular-nums text-fg-muted">
                    {rule.storeCount} store{rule.storeCount === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="paragraph-xs mt-0.5 truncate text-fg-muted">
                  {rule.description ?? "—"}
                </div>
                <div className="paragraph-xs mt-1 tabular-nums text-fg-subtle">
                  {cycleSummary(params)} · safety {dec1(params.safetyCoverDays)}d ·{" "}
                  {params.thresholdMode === "units"
                    ? `${params.unitMin}/${params.unitOpt}/${params.unitOver} u`
                    : `x ${params.overstockFactor}`}
                </div>
              </button>
            )
          })}
          {drafts[NEW_RULE_ID] !== undefined && (
            <button
              onClick={() => selectRule(NEW_RULE_ID)}
              className={cn(
                "rounded-lg border p-2.5 text-left",
                isNew ? "border-strong bg-highlight" : "border-base bg-base hover:bg-subtle",
              )}
            >
              <span className="label-base">{drafts[NEW_RULE_ID]?.name ?? "Untitled rule"}</span>
              <div className="paragraph-xs mt-0.5 text-fg-muted">Unsaved draft.</div>
            </button>
          )}
          {rules.length === 0 && drafts[NEW_RULE_ID] === undefined && (
            <div className="paragraph-xs px-2 py-3 text-fg-muted">
              No rules yet. A rule defines the top-up maths for the stores assigned to it.
            </div>
          )}
        </div>
      </div>

      {/* Detail */}
      {activeId === null ? (
        <div className="paragraph-sm flex flex-1 items-center justify-center text-fg-muted">
          Create a rule to get started.
        </div>
      ) : (
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="px-4 pt-3 pb-2">
            <div className="flex items-center gap-2.5">
              <div className="min-w-0 flex-1">
                <Input
                  small
                  requires="edit"
                  variant="transparent"
                  wrapperClassName="block"
                  containerClassName="-ml-2 w-full max-w-80"
                  className="w-full !text-[15px] !leading-[22px] font-semibold"
                  value={effectiveName}
                  onChange={(e) => setDraft({ name: e.target.value })}
                  placeholder="Rule name"
                />
              </div>
              {!isNew && selectedRule && (
                <>
                  <Button
                    size="sm"
                    variant="neutral"
                    requires="edit"
                    onClick={() =>
                      ruleSwitch.run(() =>
                        mutations.duplicateRule.mutate(
                          { ruleId: selectedRule.id },
                          {
                            onError: showErrorToast,
                            onSuccess: (r) => setSelectedRuleId(r.ruleId),
                          },
                        ),
                      )
                    }
                  >
                    <Copy size={12} />
                    Duplicate
                  </Button>
                  <ConfirmationDialog
                    requires="edit"
                    title={`Delete ${selectedRule.name}?`}
                    description={`${selectedRule.storeCount} store${selectedRule.storeCount === 1 ? "" : "s"} will be left without a rule and stop receiving proposals until reassigned.`}
                    onConfirm={() =>
                      mutations.deleteRule.mutate(
                        { ruleId: selectedRule.id },
                        {
                          onError: showErrorToast,
                          onSuccess: (result) => {
                            setSelectedRuleId(null)
                            noteRunAfterCurrent(result.run)
                          },
                        },
                      )
                    }
                    trigger={
                      <Button size="sm" variant="danger">
                        <Trash size={12} />
                        Delete
                      </Button>
                    }
                  />
                </>
              )}
            </div>
            {/* Description on its own row, same for new and created rules. */}
            <div className="mt-0.5">
              <Input
                small
                requires="edit"
                variant="transparent"
                wrapperClassName="block"
                containerClassName="-ml-2 w-full max-w-xl"
                className="w-full !text-[11.5px] !leading-[16px] font-normal !text-fg-muted"
                value={effectiveDescription ?? ""}
                onChange={(e) => setDraft({ description: e.target.value || null })}
                placeholder="Add a description"
              />
            </div>
          </div>
          {/* Standard tabs row: HeaderTabs keeps its default px-4 — the
              underline indicator's offset math assumes that padding. */}
          <HeaderTabs
            tabs={RULE_DETAIL_TABS}
            selectedTab={ruleTab === "logic" ? "Logic" : "Stores"}
            onChange={(label) => setRuleTab(label === "Logic" ? "logic" : "stores")}
          />

          <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-3.5 pb-24">
            {ruleTab === "logic" ? (
              // Keyed per rule so field drafts and invalid flags reset on switch.
              <div key={activeId} className="flex max-w-4xl flex-col gap-3.5">
                <TargetsSection
                  effective={effective}
                  saved={savedParams}
                  cardDraft={cardDraft}
                  onUpdateCard={updateCardDraft}
                  onUnitChange={setDraft}
                  onModeChange={handleModeChange}
                />
                <SettingsSection
                  effective={effective}
                  saved={savedParams}
                  locked={targetsActive}
                  unitsMode={effective.thresholdMode === "units"}
                  onEditSettingsInstead={handleEditSettingsInstead}
                  onChange={setDraft}
                  onGoToRateOfSale={onGoToRateOfSale}
                />
                <ReviewCycleSection
                  effective={effective}
                  unitsMode={effective.thresholdMode === "units"}
                  onChange={setDraft}
                />
                <LeadTimeSection assignedStores={assignedStores} />
                <SimulationSection
                  effective={effectiveWithCards}
                  assignedStores={assignedStores}
                  simLead={simLead}
                  onSimLead={setSimLead}
                />
                <AuditSection rule={selectedRule} />
              </div>
            ) : (
              <AssignmentSection
                stores={stores}
                loading={storesLoading}
                rules={rules}
                packages={packages}
                activeRuleId={activeId}
                storeRuleOf={storeRuleOf}
                newRuleName={drafts[NEW_RULE_ID]?.name ?? "New rule"}
                onAssign={(storeIds, ruleId) => {
                  if (saving) return
                  setAssignDraft((prev) => ({
                    ...prev,
                    ...Object.fromEntries(storeIds.map((storeId) => [storeId, ruleId])),
                  }))
                }}
              />
            )}
          </div>

          <DraftCommandbar
            key={activeId}
            changes={draftChanges}
            saving={saving}
            saveLabel={isNew ? "Create rule" : "Save"}
            onDiscard={discardDraft}
            onSave={save}
            switchRequest={ruleSwitch.request}
            aside={
              activeId && (
                <RuleImpactPanel
                  savedParams={savedParams}
                  draftParams={effectiveWithCards}
                  assignedStores={assignedStores}
                  onGoToStores={() => setRuleTab("stores")}
                />
              )
            }
          />
        </div>
      )}
    </div>
  )
}

function impactDeltaClass(delta: number) {
  if (delta > 0) return "text-badge-green-fg-text"
  if (delta < 0) return "text-badge-orange-fg-text"
  return "text-fg-muted"
}

function RuleImpactBody({
  impact,
  isLoading,
  isError,
  hasStores,
  onGoToStores,
}: {
  impact: ReturnType<typeof ruleImpact>
  isLoading: boolean
  isError: boolean
  hasStores: boolean
  onGoToStores: () => void
}) {
  if (isLoading) {
    return (
      <div
        className="flex animate-pulse flex-col gap-1.5"
        role="status"
        aria-label="Estimating impact"
      >
        <div className="h-4 w-24 rounded bg-component-hover" />
        <div className="h-3 w-16 rounded bg-component-hover" />
      </div>
    )
  }
  if (isError) {
    return (
      <div className="paragraph-xs text-fg-muted">
        Couldn&apos;t estimate the impact. Saving still works.
      </div>
    )
  }
  if (!hasStores) {
    return (
      <div className="paragraph-xs text-fg-muted">
        0 units. No stores carry this rule yet, so saving changes nothing today.
        <Button size="sm" variant="neutral" className="mt-1.5" onClick={onGoToStores}>
          Stores
        </Button>
      </div>
    )
  }
  if (impact.lines === 0) {
    return (
      <div className="paragraph-xs text-fg-muted">
        0 units. The stores on this rule have no proposal lines today, so saving changes nothing.
      </div>
    )
  }
  return (
    <>
      <div className="flex gap-4">
        <div>
          <div className="label-base tabular-nums">
            {impact.unitsDraft.toLocaleString()} u
            <span className={cn("paragraph-xs ml-1.5", impactDeltaClass(impact.delta))}>
              {impact.delta >= 0 ? "+" : ""}
              {impact.delta.toLocaleString()}
            </span>
          </div>
          <div className="paragraph-xs text-fg-muted">was {impact.unitsNow.toLocaleString()} u</div>
        </div>
        <div>
          <div className="label-base tabular-nums">{impact.belowDraft}</div>
          <div className="paragraph-xs text-fg-muted">lines below min</div>
        </div>
      </div>
      <div className="paragraph-xs mt-1.5 text-fg-muted">
        Moves {impact.moved} of {impact.lines} lines across {impact.stores} stores. Estimate based
        on the trailing selling rate. The next run confirms it.
      </div>
    </>
  )
}

/** The staged rule's estimated effect on today's proposals: each line it
 * governs, recomputed under saved vs draft params with its trailing rate of
 * sale and its store's lead time. */
function RuleImpactPanel({
  savedParams,
  draftParams,
  assignedStores,
  onGoToStores,
}: {
  savedParams: RuleParams
  draftParams: RuleParams
  assignedStores: StoreWithStatus[]
  onGoToStores: () => void
}) {
  const storeIds = useMemo(() => assignedStores.map((store) => store.id), [assignedStores])
  const { lines, isLoading, isError } = useRuleImpactLines(storeIds)
  const impact = useMemo(() => {
    const runAt = new Date()
    const reviewSaved = reviewDaysNow(savedParams, runAt)
    const reviewDraft = reviewDaysNow(draftParams, runAt)
    // The previous store set's lines stand in while the new set loads.
    const ruleStoreIds = new Set(storeIds)
    return ruleImpact(
      lines.filter((line) => ruleStoreIds.has(line.storeId)),
      { params: savedParams, reviewDays: reviewSaved },
      { params: draftParams, reviewDays: reviewDraft },
    )
  }, [savedParams, draftParams, storeIds, lines])

  return (
    <div className="w-72 shrink-0 border-l border-base px-3.5 py-2.5">
      <div className="label-sm mb-1.5 text-fg-muted">Impact on today&apos;s proposals</div>
      <RuleImpactBody
        impact={impact}
        isLoading={isLoading}
        isError={isError}
        hasStores={storeIds.length > 0}
        onGoToStores={onGoToStores}
      />
    </div>
  )
}

// ── Sections ───────────────────────────────────────────────────────────────

export function SectionCard({
  title,
  headerExtra,
  children,
}: {
  title: string
  headerExtra?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-base">
      <div className="flex items-center gap-2.5 border-b border-base bg-subtle px-3 py-2">
        <span className="label-sm text-fg-muted">{title}</span>
        {headerExtra}
      </div>
      {children}
    </div>
  )
}

function NumberField({
  value,
  onCommit,
  validate,
  disabled,
  decimals = 0,
  widthClassName = "w-18.5",
  className,
  onInvalidChange,
}: {
  value: number
  onCommit: (value: number) => void
  /** Invalid entries are rejected (error ring, nothing committed) and kept in
   * the box; validation is reactive, so they resolve when constraints move. */
  validate?: (value: number) => boolean
  disabled?: boolean
  decimals?: number
  /** Sizes the Input's outer container (its default is w-[140px]). */
  widthClassName?: string
  className?: string
  onInvalidChange?: (invalid: boolean) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [draft, setDraft] = useState<string | null>(null)
  const shown = draft ?? (decimals > 0 ? Number(value).toFixed(decimals) : String(value))
  const parsed = draft === null ? null : Number(draft)
  // Reactive: re-evaluated against the current constraints every render, so a
  // sibling edit that fixes the violation clears it immediately.
  const invalid =
    parsed !== null && !Number.isNaN(parsed) && validate !== undefined && !validate(parsed)

  useEffect(() => {
    onInvalidChange?.(invalid)
  }, [invalid, onInvalidChange])

  // A rejected entry left in the box auto-commits once a sibling edit makes
  // it valid (only while unfocused — active typing still commits on blur).
  useEffect(() => {
    if (draft === null || parsed === null || Number.isNaN(parsed) || invalid) return
    if (document.activeElement === inputRef.current) return
    onCommit(parsed)
    setDraft(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalid, draft])

  const commit = () => {
    if (draft === null || invalid) return
    if (parsed === null || Number.isNaN(parsed)) {
      setDraft(null)
      return
    }
    onCommit(parsed)
    setDraft(null)
  }
  return (
    <Input
      ref={inputRef}
      small
      requires="edit"
      disabled={disabled}
      value={shown}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit()
        if (e.key === "Escape") setDraft(null)
      }}
      containerClassName={widthClassName}
      className={cn("w-full text-right tabular-nums", invalid && "shadow-borders-error", className)}
    />
  )
}

/** "What the rule aims for": days mode expresses targets as days of cover
 * beyond the store's lead time (min = safety, optimal = safety + review,
 * overstock = factor x optimal); units mode pins fixed unit levels. Days-mode
 * card edits write only the separate card draft — the Target settings numbers
 * never move; invalid combinations (optimal not above min) are rejected and
 * highlighted rather than auto-adjusted. */
function TargetsSection({
  effective,
  saved,
  cardDraft,
  onUpdateCard,
  onUnitChange,
  onModeChange,
}: {
  effective: RuleParams
  saved: RuleParams
  cardDraft: CardDraft | null
  onUpdateCard: (patch: Partial<CardDraft>) => void
  onUnitChange: (patch: Draft) => void
  onModeChange: (patch: Draft) => void
}) {
  const unitsMode = effective.thresholdMode === "units"
  const review = effectiveReviewCycleDays(effective)
  const savedReview = effectiveReviewCycleDays(saved)
  const minDays = cardDraft?.coverMinDays ?? effective.safetyCoverDays
  const optDays = cardDraft?.coverOptDays ?? effective.safetyCoverDays + review
  const overDays =
    cardDraft?.coverOverDays ?? (effective.safetyCoverDays + review) * effective.overstockFactor
  const longGapClash = longestGapOverstockClash(effective, minDays, overDays)
  const [minInvalid, setMinInvalid] = useState(false)
  const [optInvalid, setOptInvalid] = useState(false)
  const [overInvalid, setOverInvalid] = useState(false)

  type TargetCard = {
    label: string
    value: number
    saved: number
    unit: string
    formula: string
    decimals: number
    commit: (v: number) => void
    validate?: (v: number) => boolean
    invalid?: boolean
    invalidNote?: string
    onInvalidChange?: (invalid: boolean) => void
  }
  const cards: TargetCard[] = unitsMode
    ? [
        {
          label: "Reorder point",
          value: effective.unitMin,
          saved: saved.unitMin,
          unit: "units",
          formula: "replenish when position drops below",
          decimals: 0,
          commit: (v: number) => onUnitChange({ unitMin: Math.max(0, Math.round(v)) }),
        },
        {
          label: "Order-up-to level",
          value: effective.unitOpt,
          saved: saved.unitOpt,
          unit: "units",
          formula: "top up to this level",
          decimals: 0,
          commit: (v: number) => onUnitChange({ unitOpt: Math.max(0, Math.round(v)) }),
        },
        {
          label: "Overstock level",
          value: effective.unitOver,
          saved: saved.unitOver,
          unit: "units",
          formula: "never exceed this level",
          decimals: 0,
          commit: (v: number) => onUnitChange({ unitOver: Math.max(0, Math.round(v)) }),
        },
      ]
    : [
        {
          label: "Reorder point cover",
          value: minDays,
          saved: saved.safetyCoverDays,
          unit: "days",
          formula: "ROP = ROS x (transit time + these days)",
          decimals: 1,
          commit: (v: number) => onUpdateCard({ coverMinDays: v }),
          validate: (v: number) => v >= 0 && v < optDays,
          onInvalidChange: setMinInvalid,
          invalidNote: `must be below order-up-to cover (${dec1(optDays)} d)`,
          invalid: minInvalid,
        },
        {
          label: "Order-up-to cover",
          value: optDays,
          saved: saved.safetyCoverDays + savedReview,
          unit: "days",
          formula: "OUL = ROS x (transit time + these days)",
          decimals: 1,
          commit: (v: number) => onUpdateCard({ coverOptDays: v }),
          validate: (v: number) => v > minDays && v < overDays,
          onInvalidChange: setOptInvalid,
          invalidNote: `must be between ROP cover (${dec1(minDays)} d) and OSL cover (${dec1(overDays)} d)`,
          invalid: optInvalid,
        },
        {
          label: "Overstock cover",
          value: overDays,
          saved: (saved.safetyCoverDays + savedReview) * saved.overstockFactor,
          unit: "days",
          formula: `overstock factor becomes x ${optDays > 0 ? (overDays / optDays).toFixed(2) : dec1(effective.overstockFactor)}`,
          decimals: 1,
          commit: (v: number) => onUpdateCard({ coverOverDays: v }),
          validate: (v: number) => v > optDays,
          onInvalidChange: setOverInvalid,
          invalidNote: `must be above order-up-to cover (${dec1(optDays)} d)`,
          invalid: overInvalid,
        },
      ]

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2.5">
        <span className="label-base">What the rule aims for</span>
        <div className="flex-1" />
        <span className="paragraph-xs text-fg-muted">Based on</span>
        <SegmentedControl
          aria-label="What the rule aims for, based on"
          size="sm"
          options={[
            { value: "days", label: "Days of stock" },
            { value: "units", label: "Unit quantity" },
          ]}
          value={effective.thresholdMode}
          onValueChange={(mode) => onModeChange({ thresholdMode: mode })}
          requires="edit"
        />
      </div>
      <div className="paragraph-xs mb-2 text-fg-muted">
        {unitsMode
          ? "Fixed unit levels applied to every store and size on this rule — the minimum display quantity still sets the floor."
          : "Cover windows on top of each store's transit time. The rule keeps every store between its reorder point and order-up-to level."}
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        {cards.map((card) => (
          <div key={card.label} className="rounded-lg border border-base p-3">
            <div className="flex items-center gap-1.5">
              <span className="label-sm text-fg-muted">{card.label}</span>
              <div className="flex-1" />
              {dec1(card.value) !== dec1(card.saved) && (
                <span className="paragraph-xs text-adjusted-fg">
                  was {card.decimals > 0 ? dec1(card.saved) : card.saved}
                </span>
              )}
            </div>
            <div className="mt-1.5 flex items-baseline gap-1.5">
              <NumberField
                value={card.value}
                decimals={card.decimals}
                onCommit={card.commit}
                validate={card.validate}
                onInvalidChange={card.onInvalidChange}
              />
              <span className="label-base text-fg-muted">{card.unit}</span>
            </div>
            <div
              className={cn(
                "paragraph-xs mt-1.5 tabular-nums",
                card.invalid ? "text-badge-red-fg-text" : "text-fg-muted",
              )}
            >
              {card.invalid ? card.invalidNote : card.formula}
            </div>
          </div>
        ))}
      </div>
      {!unitsMode && (
        <div className="paragraph-xs mt-2 text-fg-muted">
          Transit time comes from each store&apos;s metadata, so the same rule reaches its targets
          on different calendars per store.
        </div>
      )}
      {!unitsMode && longGapClash && (
        <div className="paragraph-xs mt-1.5 text-badge-orange-fg-text">
          On the longest gap between departures ({longGapClash.gap} d) the order-up-to cover is{" "}
          {dec1(longGapClash.cover)} d, above the overstock cover of {dec1(overDays)} d. Orders on
          that departure show an overstock warning.
        </div>
      )}
    </div>
  )
}

type SettingsRow = {
  label: string
  code: string
  value: number
  saved: number
  defaultValue: number
  unit: string
  decimals?: number
  disabled?: boolean
  note?: string
  commit: (v: number) => void
}

function SettingsRows({ rows }: { rows: SettingsRow[] }) {
  return (
    <>
      {rows.map((row) => {
        const isDefault = row.value === row.defaultValue
        const changed = String(row.value) !== String(row.saved)
        return (
          <div
            key={row.code}
            className={cn(
              "flex items-center gap-3 border-b border-base px-3 py-2 last:border-b-0",
              row.disabled && "opacity-60",
            )}
          >
            <span className="label-base w-48 shrink-0">{row.label}</span>
            <span className="paragraph-xs w-44 shrink-0 font-mono text-fg-muted">{row.code}</span>
            <NumberField
              value={row.value}
              decimals={row.decimals ?? 0}
              disabled={row.disabled}
              onCommit={row.commit}
            />
            <span className="paragraph-xs w-16 text-fg-muted">{row.unit}</span>
            {changed ? (
              <span className="paragraph-xs rounded bg-adjusted-bg px-1.5 py-px text-adjusted-fg">
                was {row.decimals ? dec1(row.saved) : row.saved}
              </span>
            ) : isDefault ? (
              <span className="paragraph-xs rounded bg-component px-1.5 py-px text-fg-muted">
                default
              </span>
            ) : null}
            {row.note && <span className="paragraph-xs text-fg-muted">{row.note}</span>}
          </div>
        )
      })}
    </>
  )
}

/** Two settings groups: "Target settings" are the fields the three target
 * cards drive (and lock while a card edit is active); "General settings" are
 * independent of the cards and always editable. */
function SettingsSection({
  effective,
  saved,
  locked,
  unitsMode,
  onEditSettingsInstead,
  onChange,
  onGoToRateOfSale,
}: {
  effective: RuleParams
  saved: RuleParams
  locked: boolean
  /** Unit-quantity targets pin ROP/OUL/OSL to fixed levels, so the cover-
   * driving trio (review period, safety stock, overstock factor) is inert.
   * Everything else stays live: MDQ still clamps the levels, and the ROS
   * settings still feed constrained allocation and the grid metrics. */
  unitsMode: boolean
  onEditSettingsInstead: () => void
  onChange: (patch: Draft) => void
  onGoToRateOfSale: () => void
}) {
  const { settings: rosSettings } = useRateOfSaleSettings()
  const trioDisabled = locked || unitsMode
  const targetRows: SettingsRow[] = [
    {
      label: "Safety stock (days)",
      code: "safety_cover_days",
      value: effective.safetyCoverDays,
      saved: saved.safetyCoverDays,
      defaultValue: RULE_DEFAULTS.safetyCoverDays,
      unit: "days",
      decimals: 1,
      disabled: trioDisabled,
      note: unitsMode ? "not used with unit-quantity targets" : undefined,
      commit: (v) => onChange({ safetyCoverDays: Math.max(0, v) }),
    },
    {
      label: "Overstock factor",
      code: "overstock_factor",
      value: effective.overstockFactor,
      saved: saved.overstockFactor,
      defaultValue: RULE_DEFAULTS.overstockFactor,
      unit: "x optimal",
      decimals: 1,
      disabled: trioDisabled,
      note: unitsMode ? "not used with unit-quantity targets" : undefined,
      commit: (v) => onChange({ overstockFactor: Math.max(1, v) }),
    },
  ]
  return (
    <>
      <SectionCard
        title="Target settings"
        headerExtra={
          unitsMode ? (
            <>
              <div className="flex-1" />
              <span className="paragraph-xs text-fg-muted">
                unit-quantity targets pin the levels, so these three have no effect
              </span>
            </>
          ) : locked ? (
            <>
              <div className="flex-1" />
              <span className="paragraph-xs text-badge-orange-fg-text">
                replaced by the targets above on save
              </span>
              <Button size="sm" variant="neutral" onClick={onEditSettingsInstead}>
                Edit settings instead
              </Button>
            </>
          ) : undefined
        }
      >
        <SettingsRows rows={targetRows} />
      </SectionCard>
      <SectionCard title="General settings">
        <div className="paragraph-xs flex flex-wrap items-center gap-2 bg-subtle px-3 py-2 text-fg-subtle">
          <span>
            {`${formatName("sale", true, true)} history window `}
            <b>{rosSettings ? `${rosSettings.windowDays}d` : "…"}</b>
            {" · Minimum days with inventory "}
            <b>{rosSettings ? `${rosSettings.minDaysWithStock}d` : "…"}</b>
            {", set for all rules in"}
          </span>
          <Button size="sm" variant="neutral" onClick={onGoToRateOfSale}>
            {`Settings → Rate of ${formatName("sale")}`}
          </Button>
        </div>
      </SectionCard>
    </>
  )
}

function ReviewCycleSection({
  effective,
  unitsMode,
  onChange,
}: {
  effective: RuleParams
  unitsMode: boolean
  onChange: (patch: Draft) => void
}) {
  const isSchedule = effective.cycleMode === "schedule"
  const unit = isSchedule ? "weeks" : "days"
  const fixedDays = Math.max(1, Math.round(effective.reviewCycleDays))
  const repeatValue = isSchedule ? effective.repeatIntervalWeeks : fixedDays
  const derived = deriveReviewCycleDays(effective.deliveryDays, effective.repeatIntervalWeeks)
  const gaps = derived !== null ? reviewPeriodRange(scheduleOf(effective)) : null
  const recurrenceText = isSchedule
    ? effective.deliveryDays.length > 0
      ? `Every ${effective.repeatIntervalWeeks > 1 ? `${effective.repeatIntervalWeeks} weeks on ` : ""}${effective.deliveryDays.join(", ")}`
      : "Pick at least one departure day"
    : `Every ${fixedDays} day${fixedDays === 1 ? "" : "s"}`
  const setRepeat = (v: number) =>
    onChange(
      isSchedule
        ? { cycleMode: "schedule", repeatIntervalWeeks: Math.min(8, Math.max(1, Math.round(v))) }
        : { cycleMode: "fixed", reviewCycleDays: Math.max(1, Math.round(v)) },
    )
  const setUnit = (next: "days" | "weeks") =>
    onChange(
      next === "weeks"
        ? { cycleMode: "schedule" }
        : {
            cycleMode: "fixed",
            reviewCycleDays: Math.max(1, Math.round(effectiveReviewCycleDays(effective))),
          },
    )
  return (
    <SectionCard title="Review period">
      <div className="flex flex-col gap-3 px-3 py-3">
        <div className="flex items-center gap-2.5">
          <span className="paragraph-xs text-fg-subtle">Depart every</span>
          <div className="flex items-center gap-1">
            <NumberField
              value={repeatValue}
              decimals={0}
              onCommit={setRepeat}
              widthClassName="w-14"
            />
            <div className="flex flex-col">
              <IconButton
                size="sm"
                variant="transparent"
                aria-label="Increase interval"
                requires="edit"
                onClick={() => setRepeat(repeatValue + 1)}
              >
                <CaretRight size={10} className="-rotate-90" />
              </IconButton>
              <IconButton
                size="sm"
                variant="transparent"
                aria-label="Decrease interval"
                requires="edit"
                onClick={() => setRepeat(repeatValue - 1)}
              >
                <CaretDown size={10} />
              </IconButton>
            </div>
          </div>
          <SegmentedControl
            aria-label="Depart every, in"
            size="sm"
            options={[
              { value: "days", label: "Days" },
              { value: "weeks", label: "Weeks" },
            ]}
            value={unit}
            onValueChange={setUnit}
            requires="edit"
          />
        </div>
        {isSchedule && (
          <div className="flex flex-col gap-1.5">
            <span className="paragraph-xs text-fg-subtle">Depart on</span>
            <div className="flex gap-1.5">
              {WEEKDAYS.map((day) => {
                const on = effective.deliveryDays.includes(day)
                return (
                  <IconButton
                    key={day}
                    size="sm"
                    variant={on ? "neutral" : "transparent"}
                    aria-label={day}
                    className={cn("rounded-full", on && "font-semibold")}
                    onClick={() =>
                      onChange({
                        cycleMode: "schedule",
                        deliveryDays: on
                          ? effective.deliveryDays.filter((d) => d !== day)
                          : [
                              ...WEEKDAYS.filter(
                                (d) => effective.deliveryDays.includes(d) || d === day,
                              ),
                            ],
                      })
                    }
                  >
                    {day[0]}
                  </IconButton>
                )
              })}
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2.5 border-t border-base pt-2.5">
          <span className="paragraph-xs text-fg-subtle">Cut-off time (UTC)</span>
          <Input
            small
            requires="edit"
            type="time"
            value={effective.cutoffTime}
            onChange={(e) => onChange({ cutoffTime: e.target.value })}
            className="w-28"
          />
          <span className="paragraph-xs flex-1 text-fg-muted">
            Changes made before the cut-off on a departure day ship that day. Later changes wait for
            the next departure.
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="label-base">{recurrenceText}</span>
          {isSchedule && derived !== null && (
            <span className="paragraph-xs text-fg-muted">
              → review period {reviewPeriodText(scheduleOf(effective))}
              {gaps && gaps.min !== gaps.max && ". Each order lasts until the departure after it."}
            </span>
          )}
        </div>
        {unitsMode && (
          <div className="paragraph-xs text-fg-muted">
            With unit-quantity targets the review period does not change the levels. The departure
            calendar still sets departure days, delivery dates and the cut-off.
          </div>
        )}
      </div>
    </SectionCard>
  )
}

function LeadTimeSection({ assignedStores }: { assignedStores: StoreWithStatus[] }) {
  const leads = assignedStores.map((s) => ({ lead: s.leadTimeDays, name: s.storeNumber }))
  const byLead = new Map<number, number>()
  for (const l of leads) byLead.set(l.lead, (byLead.get(l.lead) ?? 0) + 1)
  const sorted = [...byLead.entries()].sort((a, b) => a[0] - b[0])
  const min = leads.length > 0 ? leads.reduce((a, b) => (b.lead < a.lead ? b : a)) : null
  const max = leads.length > 0 ? leads.reduce((a, b) => (b.lead > a.lead ? b : a)) : null
  return (
    <SectionCard
      title="Transit time"
      headerExtra={
        <>
          <Lock size={11} className="text-fg-muted" />
          <span className="paragraph-xs text-fg-muted">
            store metadata · not editable on a rule
          </span>
          <div className="flex-1" />
          <span className="paragraph-xs text-fg-muted">
            {sorted.length > 0
              ? `${sorted.length} distinct value${sorted.length === 1 ? "" : "s"}`
              : ""}
          </span>
        </>
      }
    >
      {leads.length === 0 ? (
        <div className="paragraph-xs px-3 py-3 text-fg-muted">
          No stores on this rule yet. Transit times appear once stores are assigned.
        </div>
      ) : (
        <div className="flex flex-col gap-2.5 px-3 py-3">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="label-sm text-fg-muted">Shortest</div>
              <div className="heading-xs tabular-nums">{min!.lead} d</div>
              <div className="paragraph-xs truncate text-fg-muted">{min!.name}</div>
            </div>
            <div className="h-px w-6 shrink-0 bg-component-pressed" />
            <div className="min-w-0 flex-1 text-right">
              <div className="label-sm text-fg-muted">Longest</div>
              <div className="heading-xs tabular-nums">{max!.lead} d</div>
              <div className="paragraph-xs truncate text-fg-muted">{max!.name}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 border-t border-base pt-2">
            {sorted.map(([lead, count]) => (
              <span
                key={lead}
                className="paragraph-xs rounded border border-base bg-base px-1.5 py-px tabular-nums text-fg-subtle"
              >
                {lead} d <span className="text-fg-muted">x {count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </SectionCard>
  )
}

const SIM_ROS_STEPS = [0, 0.1, 0.25, 0.5, 0.75, 1, 1.5, 2, 3]

function SimulationSection({
  effective,
  assignedStores,
  simLead,
  onSimLead,
}: {
  effective: RuleParams
  assignedStores: StoreWithStatus[]
  simLead: number | null
  onSimLead: (lead: number) => void
}) {
  const storeLeads = [...new Set(assignedStores.map((s) => s.leadTimeDays))].sort((a, b) => a - b)
  const leadChips = storeLeads.length > 0 ? storeLeads : [1, 2, 3, 5, 7]
  const lead = simLead ?? leadChips[0] ?? 2
  const review = reviewDaysNow(effective, new Date())
  const reviewNote = hasCalendar(effective) ? " for the next departure" : ""
  const coverCards = [
    {
      label: "ROP cover",
      value: `${dec1(lead + effective.safetyCoverDays)} d`,
      formula: `transit ${lead}d + safety ${dec1(effective.safetyCoverDays)}d`,
    },
    {
      label: "OUL cover",
      value: `${dec1(lead + review + effective.safetyCoverDays)} d`,
      formula: `lead ${lead}d + review ${dec1(review)}d${reviewNote} + safety ${dec1(effective.safetyCoverDays)}d`,
    },
    {
      label: "OSL cover",
      value: `${dec1((lead + review + effective.safetyCoverDays) * effective.overstockFactor)} d`,
      formula: `OUL cover x ${effective.overstockFactor}`,
    },
  ]
  return (
    <SectionCard
      title="Simulation"
      headerExtra={
        <span className="paragraph-xs text-fg-muted">
          nothing here changes the rule. Move the inputs to see what the settings above would do
        </span>
      }
    >
      <div className="px-3 py-3">
        <div className="paragraph-xs mb-2.5 flex items-start gap-2 rounded-md border border-badge-blue-border bg-badge-blue-bg px-2.5 py-2 text-badge-blue-fg-text">
          <span className="mt-1.5 size-1 shrink-0 rounded-full bg-badge-status-sqaure-information" />
          A dry run of the settings above on one made-up SKU. Pick a transit time, then read what
          the rule would ask for at each rate of {formatName("sale")}. Nothing you touch here is
          saved.
        </div>
        <div className="mb-2.5 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="label-sm text-fg-muted">Try transit time</span>
            <div className="flex gap-1">
              {leadChips.map((chip) => (
                <Button
                  key={chip}
                  size="xs"
                  variant={lead === chip ? "neutral" : "transparent"}
                  className="rounded-full"
                  onClick={() => onSimLead(chip)}
                >
                  {chip} d
                </Button>
              ))}
            </div>
          </div>
        </div>
        <div className="paragraph-xs mb-2.5 text-fg-muted">
          One SKU, one store, transit time {lead} d. This is not a forecast.
        </div>
        <div className="mb-3 grid grid-cols-3 gap-2">
          {coverCards.map((card) => (
            <div key={card.label} className="rounded-md border border-base px-2.5 py-2">
              <div className="label-sm text-fg-muted">{card.label}</div>
              <div className="heading-xs mt-0.5 tabular-nums">{card.value}</div>
              <div className="paragraph-xs tabular-nums text-fg-muted">{card.formula}</div>
            </div>
          ))}
        </div>
        <table className="w-full overflow-hidden rounded-md border border-base">
          <thead>
            <tr className="bg-subtle">
              <th className="label-sm border-b border-base px-2.5 py-1.5 text-left text-fg-muted">
                Rate of {formatName("sale")}
              </th>
              <th className="label-sm border-b border-base px-2.5 py-1.5 text-right text-fg-muted">
                ROP
              </th>
              <th className="label-sm border-b border-base px-2.5 py-1.5 text-right text-fg-muted">
                OUL
              </th>
              <th className="label-sm border-b border-base px-2.5 py-1.5 text-right text-fg-muted">
                OSL
              </th>
              <th className="label-sm border-b border-base px-2.5 py-1.5 text-left text-fg-muted">
                Note
              </th>
            </tr>
          </thead>
          <tbody>
            {SIM_ROS_STEPS.map((ros) => {
              const levels = computeStockLevels(effective, {
                ros,
                leadTimeDays: lead,
                position: 0,
                reviewDays: review,
              })
              const note =
                ros === 0 && effective.thresholdMode !== "units"
                  ? "no sales, so only a minimum display quantity set in Settings › Products orders stock"
                  : ""
              return (
                <tr key={ros} className={cn(ros === 0 && "bg-subtle")}>
                  <td className="paragraph-xs border-b border-base px-2.5 py-1.5 font-medium tabular-nums">
                    {ros === 0 ? "0 (no sales)" : `${ros} /day`}
                  </td>
                  <td className="paragraph-xs border-b border-base px-2.5 py-1.5 text-right tabular-nums">
                    {levels.min}
                  </td>
                  <td className="paragraph-xs border-b border-base px-2.5 py-1.5 text-right font-semibold tabular-nums">
                    {levels.optimal}
                  </td>
                  <td className="paragraph-xs border-b border-base px-2.5 py-1.5 text-right tabular-nums">
                    {levels.overstock}
                  </td>
                  <td className="paragraph-xs border-b border-base px-2.5 py-1.5 text-fg-muted">
                    {note}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  )
}

function AuditSection({ rule }: { rule: EditorRuleView | null }) {
  const audits = rule?.audits ?? []
  return (
    <SectionCard title="Audit log">
      {audits.length === 0 ? (
        <div className="paragraph-xs px-3 py-3 text-fg-muted">No changes recorded yet.</div>
      ) : (
        <table className="w-full">
          <thead>
            <tr>
              {["When", "Who", "Action", "Field", "Before", "After"].map((h) => (
                <th
                  key={h}
                  className="label-sm border-b border-base px-3 py-1.5 text-left text-fg-muted"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {audits.map((audit) => (
              <tr key={audit.id}>
                <td className="paragraph-xs border-b border-base px-3 py-1.5 tabular-nums text-fg-subtle">
                  {new Date(audit.createdAt).toLocaleString(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </td>
                <td className="paragraph-xs border-b border-base px-3 py-1.5 font-medium">
                  {audit.createdBy}
                </td>
                <td className="paragraph-xs border-b border-base px-3 py-1.5 text-fg-subtle">
                  {audit.action}
                </td>
                <td className="paragraph-xs border-b border-base px-3 py-1.5 font-mono text-fg-muted">
                  {audit.field}
                </td>
                <td className="paragraph-xs border-b border-base px-3 py-1.5 tabular-nums text-fg-muted">
                  {audit.before ?? "—"}
                </td>
                <td className="paragraph-xs border-b border-base px-3 py-1.5 font-semibold tabular-nums">
                  {audit.after ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SectionCard>
  )
}

type AssignmentProps = {
  stores: StoreWithStatus[]
  loading: boolean
  rules: EditorRuleView[]
  packages: { id: string; name: string; status: string; storeIds: string[] }[]
  activeRuleId: string
  newRuleName: string
  storeRuleOf: (store: StoreWithStatus) => string | null
  onAssign: (storeIds: string[], ruleId: string | null) => void
}

type RuleAssignmentRow = StoreAssignmentRow & {
  classification: string
  transitDays: number
  ruleName: string
  packageNames: string[]
}

const RULE_ASSIGNMENT_COLUMNS: StoreAssignmentColumn<RuleAssignmentRow>[] = [
  { field: "classification", name: "Class", type: "string", className: "w-11" },
  { field: "city", name: "City", type: "string", className: "w-24 text-fg-muted" },
  {
    field: "transitDays",
    name: "Transit time",
    type: "number",
    className: "w-18 text-right tabular-nums",
    render: (row) => `${row.transitDays} d`,
  },
  { field: "ruleName", name: "Current rule", type: "string", className: "w-44" },
  {
    field: "packageNames",
    name: "Active packages",
    type: "string",
    className: "w-56 text-fg-muted",
    render: (row) => row.packageNames.join(", ") || "no active package",
  },
]

function AssignmentSection({
  stores,
  loading,
  rules,
  packages,
  activeRuleId,
  newRuleName,
  storeRuleOf,
  onAssign,
}: AssignmentProps) {
  const packageNamesByStore = useMemo(() => {
    const byStore = new Map<string, string[]>()
    for (const pkg of packages) {
      if (pkg.status !== "active") continue
      for (const storeId of pkg.storeIds)
        byStore.set(storeId, [...(byStore.get(storeId) ?? []), pkg.name])
    }
    return byStore
  }, [packages])
  const rows = useMemo<RuleAssignmentRow[]>(() => {
    const ruleName = (id: string | null) => {
      if (id === null) return "—"
      if (id === activeRuleId) return "this rule"
      if (id === NEW_RULE_ID) return newRuleName
      return rules.find((r) => r.id === id)?.name ?? "this rule"
    }
    return stores.map((store) => {
      const current = storeRuleOf(store)
      const checked = current === activeRuleId
      return {
        id: store.id,
        storeName: store.storeNumber,
        city: store.city,
        checked,
        changed: (store.replenishmentRuleId === activeRuleId) !== checked,
        classification: store.classification,
        transitDays: store.leadTimeDays,
        ruleName: ruleName(current),
        packageNames: packageNamesByStore.get(store.id) ?? [],
      }
    })
  }, [stores, rules, packageNamesByStore, activeRuleId, newRuleName, storeRuleOf])

  return (
    <div className="max-w-4xl">
      <StoreAssignmentTable
        scopeKey="allocation-planning.rule-stores"
        note="A store carries exactly one replenishment rule. Store x package pairs inherit it and can be overridden per pair in the store directory."
        rows={rows}
        columns={RULE_ASSIGNMENT_COLUMNS}
        targetLabel="this rule"
        loading={loading}
        onAssign={(storeIds, checked) => onAssign(storeIds, checked ? activeRuleId : null)}
      />
    </div>
  )
}
